import {
  BlockContextProvider,
  store as blockEditorStore,
  __experimentalUseBlockPreview as useBlockPreview,
  useBlockProps,
  useInnerBlocksProps,
} from "@wordpress/block-editor";
import { Spinner } from "@wordpress/components";
import { store as coreStore } from "@wordpress/core-data";
import { useSelect } from "@wordpress/data";
import { memo, useMemo, useState } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import clsx from "clsx";

import SliderPreview from "../../_shared/loop-slider-preview";
import "../editor.scss";

// Default post card, mirroring themes/brandy/patterns/newest-posts-grid.php so a
// fresh insert already looks like the theme's blog card.
const TEMPLATE = [
  [
    "core/post-featured-image",
    {
      isLink: true,
      style: { spacing: { margin: { bottom: "17px" } } },
    },
  ],
  [
    "core/post-date",
    {
      textAlign: "center",
      textColor: "brandy-muted-foreground",
      fontSize: "small",
      style: { spacing: { margin: { top: "0", bottom: "6px" } } },
    },
  ],
  [
    "core/post-title",
    {
      textAlign: "center",
      level: 3,
      isLink: true,
      fontSize: "large",
      style: { spacing: { margin: { top: "0", bottom: "10px" } } },
    },
  ],
];

// Preview cap: the canvas never needs more cards than the frontend shows, and
// the REST route rejects per_page above 100.
const MAX_PREVIEW = 100;

// Mirror of Helpers/LoopLayout::gap_style(): turn the block's own "Block spacing"
// value into a length the canvas can use, resolving the `var:preset|spacing|50`
// shorthand theme.json presets are stored in.
const DEFAULT_GAP = "30px"; // slider preview only; the grid falls back in CSS

function resolveBlockGap(attributes) {
  const gap = attributes?.style?.spacing?.blockGap;

  if (typeof gap !== "string" || !gap) {
    return { grid: undefined, slider: DEFAULT_GAP };
  }

  const resolved = gap.startsWith("var:preset|spacing|")
    ? `var(--wp--preset--spacing--${gap.slice("var:preset|spacing|".length)})`
    : gap;

  return { grid: resolved, slider: resolved };
}

/**
 * Translate the block's `query` context into REST parameters.
 *
 * The attribute keeps core's Query Loop shape, so this is the same mapping core's
 * own Post Template edit performs — which is what keeps the canvas honest about
 * what the frontend will render.
 */
function buildRestQuery(query = {}, taxonomies) {
  const perPage = Math.min(MAX_PREVIEW, Math.max(1, query.perPage ?? 8));

  const rest = {
    per_page: perPage,
    offset: query.offset ?? 0,
    order: query.order ?? "desc",
    orderby: query.orderBy ?? "date",
  };

  if (query.author) {
    rest.author = query.author;
  }
  if (query.search) {
    rest.search = query.search;
  }
  if (query.exclude?.length) {
    rest.exclude = query.exclude;
  }
  if (query.parents?.length) {
    rest.parent = query.parents;
  }
  if (query.sticky) {
    rest.sticky = "only" === query.sticky;
  }

  // Terms are stored by taxonomy slug; the REST route takes them by `rest_base`
  // (`category` -> `categories`, `post_tag` -> `tags`), so the registered
  // taxonomies are what translate one to the other.
  Object.entries(query.taxQuery ?? {}).forEach(([slug, terms]) => {
    if (!terms?.length) {
      return;
    }
    const taxonomy = (taxonomies ?? []).find((item) => item.slug === slug);
    if (taxonomy?.rest_base) {
      rest[taxonomy.rest_base] = terms;
    }
  });

  return rest;
}

function PostsTemplateInnerBlocks({ classList }) {
  const innerBlocksProps = useInnerBlocksProps(
    { className: clsx("wp-block-post wp-block-brandy-post", classList) },
    { template: TEMPLATE, __unstableDisableLayoutClassNames: true }
  );
  return <li {...innerBlocksProps} />;
}

function PostsTemplateBlockPreview({
  blocks,
  blockContextId,
  classList,
  isHidden,
  setActiveBlockContextId,
}) {
  const blockPreviewProps = useBlockPreview({
    blocks,
    props: { className: clsx("wp-block-post wp-block-brandy-post", classList) },
  });

  const handleOnClick = () => setActiveBlockContextId(blockContextId);

  return (
    <li
      {...blockPreviewProps}
      tabIndex={0}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role="button"
      onClick={handleOnClick}
      onKeyPress={handleOnClick}
      style={{ display: isHidden ? "none" : undefined }}
    />
  );
}

const MemoizedPostsTemplateBlockPreview = memo(PostsTemplateBlockPreview);

export default function Edit(props) {
  const { attributes, clientId, context } = props;
  const { displayLayout, slider, query } = context ?? {};
  const isSlider = !!slider?.enabled;
  const gap = resolveBlockGap(attributes);

  const columns = displayLayout?.columns ?? 3;
  const columnsTablet = displayLayout?.columnsTablet ?? 2;
  const columnsMobile = displayLayout?.columnsMobile ?? 1;
  const postType = query?.postType ?? "post";

  // The frontend's `is-slider` class is deliberately not reused here: it exists
  // to hand the layout to Embla, which never runs in the canvas. SliderPreview
  // renders a scrollable strip instead and takes its widths from the same
  // --embla-slides-* / --embla-gap properties the frontend slider uses, so both
  // sets of custom properties are published on the wrapper in slider mode.
  const blockProps = useBlockProps({
    style: {
      "--bpc-columns": columns,
      "--bpc-columns-tablet": columnsTablet,
      "--bpc-columns-mobile": columnsMobile,
      ...(gap.grid ? { "--bpc-gap": gap.grid, "--bpc-row-gap": gap.grid } : {}),
      ...(isSlider
        ? {
            "--embla-slides-desktop": columns,
            "--embla-slides-tablet": columnsTablet,
            "--embla-slides-mobile": columnsMobile,
            "--embla-gap": gap.slider,
            // Dots are hidden by default via this token; the preview shows them
            // only when the block asks for them, exactly like the frontend.
            "--embla-pagination-display": slider?.showDots ? "flex" : "none",
          }
        : {}),
    },
  });

  const [activeBlockContextId, setActiveBlockContextId] = useState();

  const queryJson = useMemo(() => JSON.stringify(query ?? {}), [query]);

  const { posts, blocks } = useSelect(
    (select) => {
      const { getEntityRecords, getTaxonomies } = select(coreStore);
      const { getBlocks } = select(blockEditorStore);

      const taxonomies = getTaxonomies({ type: postType, per_page: -1 });

      return {
        posts: getEntityRecords("postType", postType, {
          ...buildRestQuery(JSON.parse(queryJson), taxonomies),
          _locale: "user",
        }),
        blocks: getBlocks(clientId),
      };
    },
    [clientId, queryJson, postType]
  );

  const blockContexts = useMemo(
    () =>
      posts?.map((post) => ({
        postType: post.type,
        postId: post.id,
        // The rendered items carry get_post_class(); the REST record exposes the
        // same list, so a preview card is styled like the published one.
        classList: post.class_list ?? "",
      })),
    [posts]
  );

  if (!posts) {
    return (
      <p {...blockProps}>
        <Spinner />
      </p>
    );
  }

  // Nothing matches yet (an empty site, or filters that exclude everything).
  // Still render the template once so the admin can build it; the child blocks
  // fall back to their own placeholders.
  if (!posts.length) {
    return (
      <>
        <p className="wp-block-brandy-posts-template__notice">
          {__(
            "No posts match this query yet. The card below is a preview and stays editable — it will apply to every post once there are results.",
            "brandy-blocks"
          )}
        </p>
        <ul {...blockProps}>
          <BlockContextProvider value={{ postType, postId: 0 }}>
            <PostsTemplateInnerBlocks />
          </BlockContextProvider>
        </ul>
      </>
    );
  }

  const items = (blockContexts ?? []).map((blockContext) => (
    <BlockContextProvider key={blockContext.postId} value={blockContext}>
      {blockContext.postId ===
      (activeBlockContextId || blockContexts[0]?.postId) ? (
        <PostsTemplateInnerBlocks classList={blockContext.classList} />
      ) : null}
      <MemoizedPostsTemplateBlockPreview
        blocks={blocks}
        blockContextId={blockContext.postId}
        classList={blockContext.classList}
        setActiveBlockContextId={setActiveBlockContextId}
        isHidden={
          blockContext.postId ===
          (activeBlockContextId || blockContexts[0]?.postId)
        }
      />
    </BlockContextProvider>
  ));

  if (isSlider) {
    return (
      <SliderPreview
        blockProps={blockProps}
        slider={slider}
        slidesPerView={columns}
        slideCount={posts.length}
      >
        {items}
      </SliderPreview>
    );
  }

  return <ul {...blockProps}>{items}</ul>;
}
